/**
 * Adjusting where a connector's bends sit (D23), as in draw.io and Lucid.
 *
 * A selected edge offers a handle on each segment that can move. Dragging a
 * vertical segment moves it left or right, a horizontal one up or down; its
 * neighbours stretch to stay attached, so the path stays right-angled. The
 * result is stored as the edge's `schematic_waypoints` — presentation only.
 *
 * The segments running straight out of each port (the stubs) do not move:
 * they are what keeps a line entering its port head-on. Moving the segment
 * next to a stub inserts a new corner rather than dragging the stub sideways,
 * which is also how a straight line gets its first jog.
 */
import type { Point, SceneEdge } from "../scene/types";
import { DEFAULT_ROUTE_OPTIONS, simplify, stubOf } from "../scene/route";

export type Orientation = "horizontal" | "vertical";

export interface SegmentHandle {
  /** Index into `expandedPath(edge)`: the segment from point `index` to `index + 1`. */
  index: number;
  /** Where to draw the handle: the middle of the segment. */
  point: Point;
  orientation: Orientation;
}

/** Segments shorter than this get no handle: there is nothing to grab. */
const MIN_HANDLE_LENGTH = 6;

/**
 * The edge's path with its stubs as explicit points: port, stub, bends, stub,
 * port. The scene simplifies collinear points away, which merges a stub into
 * the segment beyond it; editing needs them back so it can leave them fixed.
 */
export function expandedPath(edge: SceneEdge): Point[] {
  const path = edge.path;
  if (path.length < 2) return [...path];
  const start = stubOf(edge.from, DEFAULT_ROUTE_OPTIONS.stub);
  const goal = stubOf(edge.to, DEFAULT_ROUTE_OPTIONS.stub);
  const points = [path[0]!, start, ...path.slice(1, -1), goal, path[path.length - 1]!];
  // Drop exact repeats only; collinear points stay, since they are the stubs.
  return points.filter((point, i) => i === 0 || point.x !== points[i - 1]!.x || point.y !== points[i - 1]!.y);
}

function orientationOf(a: Point, b: Point): Orientation | null {
  if (a.y === b.y && a.x !== b.x) return "horizontal";
  if (a.x === b.x && a.y !== b.y) return "vertical";
  return null;
}

/** The segments of an edge that can be dragged, with where their handles go. */
export function segmentHandles(edge: SceneEdge): SegmentHandle[] {
  const path = expandedPath(edge);
  const handles: SegmentHandle[] = [];
  // Not the first or last segment: those are the stubs out of each port.
  for (let index = 1; index <= path.length - 3; index += 1) {
    const a = path[index]!;
    const b = path[index + 1]!;
    const orientation = orientationOf(a, b);
    if (!orientation) continue;
    if (Math.abs(a.x - b.x) + Math.abs(a.y - b.y) < MIN_HANDLE_LENGTH) continue;
    handles.push({ index, point: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, orientation });
  }
  return handles;
}

/**
 * The waypoints after moving one segment across by `offset` (scene units,
 * along the segment's normal). `snap` rounds the segment's new position to
 * the grid, as moving a component does.
 */
export function moveSegment(
  edge: SceneEdge,
  index: number,
  offset: number,
  snap: (value: number) => number = (value) => value,
): Point[] {
  const path = expandedPath(edge);
  const a = path[index];
  const b = path[index + 1];
  const orientation = a && b ? orientationOf(a, b) : null;
  if (!a || !b || !orientation || index < 1 || index > path.length - 3) {
    return waypointsOf(path);
  }
  let first = index;
  // Next to a stub: keep the stub where it is and move a new corner instead.
  if (first === 1) {
    path.splice(1, 0, { ...path[1]! });
    first += 1;
  }
  if (first + 1 === path.length - 2) {
    path.splice(first + 2, 0, { ...path[first + 1]! });
  }
  const along = orientation === "vertical" ? "x" : "y";
  const moved = snap(path[first]![along] + offset);
  path[first] = { ...path[first]!, [along]: moved };
  path[first + 1] = { ...path[first + 1]!, [along]: moved };
  return waypointsOf(path);
}

/** The bends to store: everything between the two ports, simplified. */
function waypointsOf(path: readonly Point[]): Point[] {
  return simplify(path).slice(1, -1);
}
